import { useEffect, useId, useRef, useState } from 'react';

import {
  locationsApi,
  type AddressAutocompleteSuggestion,
} from '../../features/locations/locations.api';

import styles from './AddressAutocompleteField.module.css';

const MINIMUM_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 350;

interface AddressAutocompleteFieldProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onSelect: (suggestion: AddressAutocompleteSuggestion) => void;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly name?: string;
  readonly label?: string;
}

/**
 * A reusable, progressively-enhanced address line input. Every outcome keeps
 * its text box usable, including provider failure, so manual entry is always
 * a first-class path.
 */
export function AddressAutocompleteField({
  value,
  onChange,
  onSelect,
  disabled = false,
  required = false,
  name = 'addressLine1',
  label = 'Address line 1',
}: AddressAutocompleteFieldProps): React.JSX.Element {
  const inputId = useId();
  const listboxId = `${inputId}-suggestions`;
  const requestSequence = useRef(0);
  const skipNextLookupForValue = useRef<string | null>(null);
  const [suggestions, setSuggestions] = useState<readonly AddressAutocompleteSuggestion[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'empty' | 'unavailable'>('idle');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const query = value.trim();
    const requestId = ++requestSequence.current;

    // Selecting an option deliberately changes the text value. That update
    // must not immediately issue a second lookup or reopen the dropdown.
    if (skipNextLookupForValue.current === query) {
      skipNextLookupForValue.current = null;
      setSuggestions([]);
      setStatus('idle');
      setActiveIndex(-1);
      return;
    }

    if (query.length < MINIMUM_QUERY_LENGTH) {
      setSuggestions([]);
      setStatus('idle');
      setActiveIndex(-1);
      return;
    }

    // Results belong to the previous query until the new request resolves.
    // Hide them immediately, including during debounce, so they cannot be selected.
    setSuggestions([]);
    setStatus('idle');
    setActiveIndex(-1);

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setStatus('loading');
      void locationsApi
        .addressAutocomplete(query, controller.signal)
        .then((response) => {
          if (requestSequence.current !== requestId) return;

          const nextSuggestions = response.suggestions.slice(0, 5);
          setSuggestions(nextSuggestions);
          setStatus(nextSuggestions.length === 0 ? 'empty' : 'idle');
          setActiveIndex(-1);
        })
        .catch(() => {
          if (requestSequence.current !== requestId || controller.signal.aborted) return;

          setSuggestions([]);
          setStatus('unavailable');
          setActiveIndex(-1);
        });
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [value]);

  const selectSuggestion = (suggestion: AddressAutocompleteSuggestion): void => {
    skipNextLookupForValue.current = (suggestion.line1 ?? suggestion.displayAddress).trim();
    onSelect(suggestion);
    setSuggestions([]);
    setStatus('idle');
    setActiveIndex(-1);
  };

  const canShowList = isFocused && suggestions.length > 0;
  const statusMessage =
    status === 'loading'
      ? 'Looking up address suggestions…'
      : status === 'empty'
        ? 'No matching addresses found. You can enter your address manually.'
        : status === 'unavailable'
          ? 'Suggestions are unavailable right now. You can enter your address manually.'
          : queryHelp(value);

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={inputId}>
        {label}
        {required && (
          <span className={styles.required} aria-hidden="true">
            {' '}
            *
          </span>
        )}
        {required && <span className="visually-hidden"> (required)</span>}
      </label>

      <div className={styles.inputWrap}>
        <input
          id={inputId}
          className={styles.input}
          name={name}
          value={value}
          required={required}
          disabled={disabled}
          autoComplete="address-line1"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={canShowList}
          aria-controls={canShowList ? listboxId : undefined}
          aria-activedescendant={
            activeIndex >= 0 && canShowList ? `${inputId}-suggestion-${activeIndex}` : undefined
          }
          onChange={(event) => {
            onChange(event.target.value);
          }}
          onFocus={() => {
            setIsFocused(true);
          }}
          onBlur={() => {
            // Let a pointer selection reach its button before collapsing the list.
            window.setTimeout(() => setIsFocused(false), 120);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' && suggestions.length > 0) {
              event.preventDefault();
              setIsFocused(true);
              setActiveIndex((current) => (current + 1) % suggestions.length);
              return;
            }

            if (event.key === 'ArrowUp' && suggestions.length > 0) {
              event.preventDefault();
              setIsFocused(true);
              setActiveIndex((current) => (current <= 0 ? suggestions.length - 1 : current - 1));
              return;
            }

            if (
              event.key === 'Enter' &&
              activeIndex >= 0 &&
              suggestions[activeIndex] !== undefined
            ) {
              event.preventDefault();
              selectSuggestion(suggestions[activeIndex]);
              return;
            }

            if (event.key === 'Escape') {
              setSuggestions([]);
              setActiveIndex(-1);
              setIsFocused(false);
            }
          }}
        />

        {canShowList && (
          <ul
            className={styles.suggestions}
            id={listboxId}
            role="listbox"
            aria-label="Address suggestions"
          >
            {suggestions.map((suggestion, index) => (
              <li
                key={`${suggestion.providerPlaceId ?? suggestion.displayAddress}-${index}`}
                role="option"
                aria-selected={activeIndex === index}
              >
                <button
                  id={`${inputId}-suggestion-${index}`}
                  type="button"
                  className={activeIndex === index ? styles.suggestionActive : styles.suggestion}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    selectSuggestion(suggestion);
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <span>{suggestion.displayAddress}</span>
                  <span className={styles.suggestionContext}>
                    {[suggestion.city, suggestion.stateCode?.replace(/_/g, ' ')]
                      .filter(Boolean)
                      .join(', ')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className={styles.hint} aria-live="polite">
        {statusMessage}
      </p>
    </div>
  );
}

function queryHelp(value: string): string {
  return value.trim().length < MINIMUM_QUERY_LENGTH
    ? 'Type at least 3 characters for suggestions, or enter your address manually.'
    : 'Choose a suggestion or keep typing your address manually.';
}
